import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import clinicAPI from "../../services/clinicApi";
import { Button } from "../ui";
import ConsentCheckbox from "./ConsentCheckbox";
import { LegalDocLink } from "./LegalDocLink";
import { LEGAL_PUBLIC_ENABLED } from "../../constants/legalPaths";
import { clientNeedsConsent } from "../../utils/legalAcceptance";
import { normalizeRole } from "../../utils/roles";
import s from "./legalGates.module.css";

/** Блокирующий догон согласия ПДн для клиентов клуба. */
export default function ClientConsentGate() {
  const location = useLocation();
  const { isAuth, role } = useSelector((state) => state.auth);
  const [card, setCard] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!LEGAL_PUBLIC_ENABLED || !isAuth || normalizeRole(role) !== "client") {
      setLoaded(true);
      setCard(null);
      return;
    }
    try {
      const data = await clinicAPI.getMyPatientProfile();
      setCard(data?.patient?.patient_card || null);
    } catch {
      setCard(null);
    } finally {
      setLoaded(true);
    }
  }, [isAuth, role]);

  useEffect(() => {
    load();
  }, [load]);

  const onLegalPage = location.pathname.startsWith("/legal");

  if (
    !LEGAL_PUBLIC_ENABLED ||
    !isAuth ||
    normalizeRole(role) !== "client" ||
    onLegalPage
  ) {
    return null;
  }
  if (!loaded || !clientNeedsConsent(card)) return null;

  const submit = async () => {
    if (!checked) return;
    setLoading(true);
    setError("");
    try {
      const data = await clinicAPI.updateMyPatientProfile({ consent_pd: true });
      setCard(data?.patient?.patient_card || { consent_pd: true });
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e?.message ||
          "Не удалось сохранить согласие",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={s.overlay} role="dialog" aria-modal="true">
      <div className={s.modal}>
        <h2 className={s.title}>Согласие на обработку данных</h2>
        <p className={s.lead}>
          Чтобы пользоваться личным кабинетом, подтвердите согласие на обработку
          персональных данных клубом.
        </p>
        <ConsentCheckbox
          id="client-gate-consent-pd"
          checked={checked}
          onChange={setChecked}
          disabled={loading}
        >
          Даю согласие на обработку персональных данных согласно{" "}
          <LegalDocLink doc="clientConsent" target="_blank" rel="noopener noreferrer">
            тексту согласия
          </LegalDocLink>
        </ConsentCheckbox>
        {error ? <p className={s.error}>{error}</p> : null}
        <div className={s.actions}>
          <Button
            type="button"
            block
            size="lg"
            disabled={!checked || loading}
            onClick={submit}
          >
            {loading ? "Сохраняем…" : "Продолжить"}
          </Button>
        </div>
      </div>
    </div>
  );
}
